import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Users, UserPlus, Calendar, MapPin, MessageSquare, FileText, Video, Download } from 'lucide-react';
import { boardMembers, organizationInfo, boardMeetingInfo } from '../mockData';

const BoardPage = () => {
  return (
    <div className="pt-20">
      {/* Header */}
      <section className="py-16 bg-gray-50">
        <div className="container text-center">
          <h1 className="heading-1 mb-6">Our Board of Directors</h1>
          <p className="body-large max-w-3xl mx-auto">
            Saged Recovery and Wellness Collaborative is guided by a dedicated board that brings together
            lived experience, professional expertise, and a shared commitment to building recovery‑friendly
            communities across Manistee and Mason Counties.
          </p>
        </div>
      </section>

      {/* Board members */}
      <section className="py-16">
        <div className="container">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            {boardMembers.map((member) => (
              <div
                key={member.id}
                data-testid={`board-member-${member.id}`}
                className={`p-8 rounded-lg border shadow-sm flex flex-col sm:flex-row gap-6 ${
                  member.vacant
                    ? 'bg-gray-50 border-dashed border-green-300'
                    : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex-shrink-0 mx-auto sm:mx-0">
                  {member.photo ? (
                    <img
                      src={member.photo}
                      alt={member.name}
                      loading="lazy"
                      style={{ objectPosition: member.photoPosition || 'center' }}
                      className="w-32 h-32 rounded-full object-cover border-4 border-green-100"
                    />
                  ) : (
                    <div className={`w-32 h-32 rounded-full flex items-center justify-center ${member.vacant ? 'bg-white border-2 border-dashed border-green-300' : 'bg-green-100'}`}>
                      {member.vacant ? (
                        <UserPlus className="h-14 w-14 text-green-500" />
                      ) : (
                        <Users className="h-16 w-16 text-green-600" />
                      )}
                    </div>
                  )}
                </div>
                <div className="text-center sm:text-left flex flex-col justify-center">
                  <h3 className="heading-3 mb-1">{member.vacant ? `${member.name} \u2014 Open Seat` : member.name}</h3>
                  <p className="font-semibold mb-3" style={{ color: 'var(--accent-text)' }}>{member.title}</p>
                  {member.vacant ? (
                    <p className="body-medium text-gray-600">
                      Cyndi Jacobi is serving as Acting Treasurer until this position is filled. We're seeking a
                      community member to serve as Treasurer. Interested in giving back?{' '}
                      <a href={`mailto:${organizationInfo.email}`} className="text-green-600 font-medium hover:text-green-700">Get in touch</a>.
                    </p>
                  ) : member.bio ? (
                    <p className="body-medium whitespace-pre-line">{member.bio}</p>
                  ) : (
                    <p className="body-small text-gray-500 italic">Full bio coming soon.</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Board Meetings & Public Participation */}
      <section className="py-16 bg-gray-50" data-testid="board-meetings-section">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="heading-2 mb-4">Board Meetings & Public Participation</h2>
              <p className="body-large">{boardMeetingInfo.intro}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-lg border border-gray-200 shadow-sm" data-testid="next-meeting-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 rounded-full bg-green-100">
                    <Calendar className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="service-card-title">Next Board Meeting</h3>
                </div>
                <div className="space-y-3">
                  <p className="body-medium font-semibold">
                    {boardMeetingInfo.nextMeeting.date ? `${boardMeetingInfo.nextMeeting.date} at ` : 'Board meetings are held at '}{boardMeetingInfo.nextMeeting.time}
                  </p>
                  <p className="body-medium flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    {boardMeetingInfo.nextMeeting.location}
                  </p>
                  {boardMeetingInfo.nextMeeting.zoom ? (
                    <div className="pt-2 border-t border-gray-100">
                      <a
                        href={boardMeetingInfo.nextMeeting.zoom.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="zoom-join-link"
                        className="inline-flex items-center gap-2 text-green-700 font-semibold hover:text-green-800"
                      >
                        <Video className="h-4 w-4" />
                        Join Virtually via Zoom
                      </a>
                      <p className="body-small text-gray-600 mt-2">
                        Meeting ID: {boardMeetingInfo.nextMeeting.zoom.meetingId} · Passcode: {boardMeetingInfo.nextMeeting.zoom.passcode}
                      </p>
                    </div>
                  ) : (
                    <p className="body-small text-gray-600">{boardMeetingInfo.nextMeeting.virtualNote}</p>
                  )}
                </div>
              </div>

              <div className="bg-white p-8 rounded-lg border border-gray-200 shadow-sm" data-testid="public-comment-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 rounded-full bg-green-100">
                    <MessageSquare className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="service-card-title">Public Comment Welcome</h3>
                </div>
                <p className="body-medium">{boardMeetingInfo.publicComment}</p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg border border-gray-200 shadow-sm mt-8" data-testid="minutes-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-3 rounded-full bg-green-100">
                  <FileText className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="service-card-title">Minutes & Reports</h3>
              </div>
              <p className="body-medium text-gray-600 mb-6">{boardMeetingInfo.minutesNote}</p>
              <ul className="space-y-3">
                {boardMeetingInfo.minutes.map((doc, index) => (
                  <li key={index}>
                    <a
                      href={doc.file}
                      download
                      data-testid={`minutes-download-${index}`}
                      className="flex items-center justify-between gap-3 p-4 rounded-lg border border-gray-200 hover:border-green-400 hover:bg-green-50 transition-colors group"
                    >
                      <span className="body-medium font-medium">{doc.title}</span>
                      <span className="inline-flex items-center gap-1.5 text-green-700 body-small font-semibold flex-shrink-0">
                        <Download className="h-4 w-4" />
                        Download
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Join CTA */}
      <section className="py-16 bg-green-50">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-2 mb-4">Interested in Serving on Our Board?</h2>
            <p className="body-large mb-8">
              As we grow, we welcome passionate community members who want to help shape the future of
              recovery support in our region. Reach out to learn more.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`mailto:${organizationInfo.email}`} className="btn-primary flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email {organizationInfo.email}
              </a>
              <Link to="/contact" className="btn-secondary">Contact Us</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BoardPage;
